import type { MouseEvent, ReactNode } from 'react';
import type { ButtonProps } from '../Button/Button';
import type { LinkButtonProps } from '../LinkButton/LinkButton';

import { useMemo } from 'react';
import { Link } from 'react-router';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

const colorClasses = {
  blue: 'bg-blue-50 text-slate-700 dark:bg-slate-950 dark:border-blue-600 dark:text-blue-300',
  lime: 'bg-lime-50 text-slate-700 dark:bg-slate-950 dark:border-lime-700 dark:text-lime-300',
  green:
    'bg-lime-50 text-slate-700 dark:bg-slate-950 dark:border-lime-700 dark:text-lime-300',
  red: 'bg-red-100 text-slate-700 dark:bg-slate-950 dark:border-red-900 dark:text-red-300',
  orange:
    'bg-orange-200 text-slate-700 dark:bg-slate-950 dark:border-orange-800 dark:text-orange-400',
  draft:
    'bg-blue-50 text-blue-600 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-200',
  modified:
    'bg-orange-100 text-orange-900 dark:bg-orange-950 dark:border-orange-900 dark:text-orange-200',
  public:
    'bg-lime-50 text-lime-800 dark:bg-lime-950 dark:border-lime-800 dark:text-lime-200',
  archived:
    'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300',
  scheduled:
    'bg-indigo-100 text-indigo-900 dark:bg-indigo-950 dark:border-indigo-800 dark:text-indigo-300',
  saved:
    'bg-slate-100 text-black dark:text-slate-100 dark:border-slate-100 dark:bg-transparent',
};

const sizeClasses = {
  base: 'py-1.5 px-3 h-7 text-sm',
  sm: 'h-[20px] py-0 px-[10px] w-max text-xs',
};

export type StatusPillColors = keyof typeof colorClasses;
export type StatusPillSize = keyof typeof sizeClasses;

interface StatusPillBaseProps {
  /**
   * Status of the pill
   */
  status: ReactNode;
  /**
   * Color of the pill
   */
  color?: StatusPillColors;
  /**
   * Status pill size
   */
  size?: StatusPillSize;
  /**
   * On click callback
   */
  onClick?: (
    e: MouseEvent<HTMLButtonElement | HTMLDivElement | HTMLAnchorElement>,
  ) => void;
  /**
   * Pill container additional classes
   */
  containerClasses?: string;
  /**
   * Pill test id
   */
  testId?: string;
}

type StatusPillItemProps =
  | (StatusPillBaseProps &
      Omit<LinkButtonProps, keyof StatusPillBaseProps> & {
        /**
         * Pill link
         */
        link: string;
      })
  | (StatusPillBaseProps &
      Omit<ButtonProps, keyof StatusPillBaseProps> & {
        /**
         * Pill link
         */
        link?: never;
      });

const StatusPill = ({
  status,
  color = 'blue',
  onClick,
  link,
  size = 'base',
  containerClasses = '',
  testId = '',
}: StatusPillItemProps) => {
  const baseProps = useMemo(
    () => ({
      className: twMerge(
        'w-fit max-w-full rounded-md font-normal text-slate-700 leading-tight!',
        'focus:no-underline active:no-underline hover:no-underline',
        'focus:text-slate-700 active:text-slate-700 dark:border',
        sizeClasses[size],
        (onClick || link) &&
          'cursor-pointer transition-shadow hover:drop-shadow-md hover:text-slate-700 ',
        colorClasses[color],
        containerClasses,
      ),
      onClick,
      ...getTestProps(testId, 'container'),
    }),
    [size, onClick, link, color, containerClasses, testId],
  );

  const children = (
    <span className="align-middle min-w-0 truncate">{status}</span>
  );

  if (link) {
    return (
      <Link to={link} {...baseProps}>
        {children}
      </Link>
    );
  }

  if (onClick) {
    return <button {...baseProps}>{children}</button>;
  }

  return <div {...baseProps}>{children}</div>;
};

export default StatusPill;
